import { useCallback, useContext, useMemo } from "react";
import { ContractContext } from "../../../providers/contract";
import styles from "./styles.module.scss";
import BackButton from "../../../components/contract/backButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import CashoutCard from "../CashoutCard";
import {
  AddOnFees,
  EContractType,
  ESenderRole,
  FeeCalculationType,
} from "../../../setup/api/services/contract/models";

const CashoutDetails = () => {
  const { contract } = useContext(ContractContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleBackButtonClick = () => {
    const searchParams = new URLSearchParams(location?.search);
    const back = searchParams?.get("back")?.trim();
    if (searchParams?.get("isOperation") === "true") {
      if (back === "settlements") {
        navigate(ROUTES.SETTLEMENTS);
        return;
      }
      navigate(ROUTES.COMPLETED_TRANSFERS);
      return;
    }
    navigate(ROUTES.CASHOUTS);
  };

  const handleContractClick = () => {
    navigate(`${ROUTES.CONTRACT}${contract?.id}`);
  };

  const renderQuickTransactionDetails = useCallback(() => {
    return (
      <div>
        <p className={styles["hint2"]}>{t("lbl_total_amount")}</p>
        <h1 className={classNames(styles["h1"], styles["g900"])}>
          {(contract?.cashOutAmountList
            ? contract.cashOutAmountList
                .reduce((acc, item) => acc + item.cashOutAmount, 0)
                .toFixed(2)
            : "0") +
            " " +
            t("lbl_saudi_currency")}
        </h1>
      </div>
    );
  }, [contract?.cashOutAmountList, t]);

  const maxAmount = useMemo(() => {
    if (!contract || !contract.parties) return 0;
    if (contract.type === EContractType.BROKERAGE_CONTRACT)
      return contract.itemPrice;
    let totalAmount = 0;
    const customers = contract.parties?.filter(
      (p) => p.role === ESenderRole.CUSTOMER
    );

    customers?.forEach((customer) => {
      let addOnFees = 0;
      const baseAmount = customer?.amount || 0;

      const currentCustomerAddOnFees = contract?.addonFeesList?.filter(
        (fee: AddOnFees) => fee?.payee === customer.phoneNumber
      );

      currentCustomerAddOnFees?.forEach((addOnFee) => {
        if (addOnFee.feeType === FeeCalculationType.FLAT) {
          addOnFees += addOnFee.amount;
        } else {
          addOnFees += (baseAmount * addOnFee.amount) / 100;
        }
      });

      totalAmount += baseAmount + addOnFees;
    });

    return totalAmount;
  }, [contract]);

  const renderMilestoneDetails = useCallback(() => {
    return (
      <div className={styles.amounts_container}>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_total_contract_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["buttonActive"])}>
            {maxAmount.toFixed(2) + " " + t("lbl_saudi_currency")}
          </h3>
        </div>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_waffy_fees_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["g900"])}>
            {contract?.fee.toFixed(2) + " " + t("lbl_saudi_currency")}
          </h3>
        </div>
        <div className={styles.amount_item}>
          <p className={styles["hint2"]}>{t("lbl_transfer_amount")}</p>
          <h3 className={classNames(styles["h3"], styles["buttonActive"])}>
            {(maxAmount - (contract?.fee || 0)).toFixed(2) +
              " " +
              t("lbl_saudi_currency")}
          </h3>
        </div>
      </div>
    );
  }, [contract?.fee, maxAmount, t]);

  return (
    <div className={styles.outer_container}>
      <div className={styles.container}>
        <div className={classNames(styles.top_section, styles.card)}>
          <BackButton onClick={handleBackButtonClick} />
          <div className={styles.content}>
            {contract?.type === EContractType.QUICK_TRANSACTION
              ? renderQuickTransactionDetails()
              : renderMilestoneDetails()}
            <div className={styles.details_container}>
              <p className={styles["hint2"]}>
                {t("lbl_contract_linked_to_transfer")}
              </p>
              <p className={styles.details_link} onClick={handleContractClick}>
                {t("lbl_view_contract")}
              </p>
            </div>
          </div>
        </div>

        {contract?.cashOutAmountList?.map((cashOutAmount, index) => {
          return (
            <CashoutCard
              key={index}
              cashOutAmount={cashOutAmount}
              hideFees={contract?.type !== EContractType.QUICK_TRANSACTION}
              disableCashout={cashOutAmount?.multiCashOut}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CashoutDetails;
